import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const PARTICLES = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 3 + 1,
  dur: Math.random() * 6 + 4,
  delay: Math.random() * 4,
}));

function buildWelcomeText(name) {
  return `Welcome to NeuralOps, ${name}. Access granted. You are now part of Wissen Technology's AI Center of Excellence. The mission starts now. Explore the Quest Board, claim your first challenge, and start earning recognition for the work that matters. We're glad to have you on the team.`;
}

export default function Welcome() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const name = searchParams.get('name') || 'Operative';
  const [phase, setPhase] = useState('intro'); // intro | speaking | done
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [scanLine, setScanLine] = useState(0);
  const scanRef = useRef(null);

  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      setVoiceSupported(false);
    }
  }, []);

  useEffect(() => {
    const id = setInterval(() => setScanLine(l => (l + 1) % 100), 30);
    return () => clearInterval(id);
  }, []);

  const playWelcome = () => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(buildWelcomeText(name));
    utter.rate = 0.92;
    utter.pitch = 0.85;
    utter.volume = 1;

    // Prefer a deep/authoritative voice if available
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => /google uk english male|daniel|alex|samantha/i.test(v.name))
      || voices.find(v => v.lang === 'en-US' && !v.name.toLowerCase().includes('female'))
      || voices[0];
    if (preferred) utter.voice = preferred;

    utter.onstart = () => { setSpeaking(true); setPhase('speaking'); };
    utter.onend = () => { setSpeaking(false); setPhase('done'); };
    utter.onerror = () => { setSpeaking(false); setPhase('done'); };

    window.speechSynthesis.speak(utter);
  };

  // Auto-play on mount after a short dramatic pause
  useEffect(() => {
    const t = setTimeout(() => {
      setPhase('speaking');
      playWelcome();
    }, 1600);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  const stop = () => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPhase('done');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#020408', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes scanMove { from { top: -4px; } to { top: 100%; } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes glow { 0%,100% { box-shadow: 0 0 20px rgba(0,212,255,0.3), 0 0 60px rgba(0,212,255,0.1); } 50% { box-shadow: 0 0 40px rgba(0,212,255,0.6), 0 0 100px rgba(0,212,255,0.2); } }
        @keyframes ringPulse { 0%,100% { transform:scale(1); opacity:0.6; } 50% { transform:scale(1.08); opacity:1; } }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes speakPulse { 0%,100% { transform:scaleY(0.3); } 50% { transform:scaleY(1); } }
        @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-12px); } }
        @keyframes blink { 0%,100% { opacity:1; } 50% { opacity:0.2; } }
      `}</style>

      {/* Background particles */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {PARTICLES.map(p => (
          <div key={p.id} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, borderRadius: '50%', background: 'rgba(0,212,255,0.4)', animation: `float ${p.dur}s ${p.delay}s ease-in-out infinite` }} />
        ))}
        {/* Grid overlay */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,212,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,212,255,0.03) 1px,transparent 1px)', backgroundSize: '60px 60px' }} />
        {/* Scan line */}
        <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,rgba(0,212,255,0.5),transparent)', top: `${scanLine}%`, transition: 'top 0.03s linear', pointerEvents: 'none' }} />
      </div>

      {/* Main card */}
      <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '0 24px', maxWidth: 680, animation: 'fadeUp 0.8s ease' }}>

        {/* Header */}
        <div style={{ fontSize: 10, color: 'rgba(0,212,255,0.6)', fontFamily: 'Share Tech Mono', letterSpacing: 4, marginBottom: 32, textTransform: 'uppercase' }}>
          NEURALOPS · AI CENTER OF EXCELLENCE · ACCESS GRANTED
        </div>

        {/* Orb */}
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 40 }}>
          {/* Outer rings */}
          {[120, 100, 84].map((sz, i) => (
            <div key={i} style={{ position: 'absolute', width: sz, height: sz, borderRadius: '50%', border: `1px solid rgba(0,212,255,${0.15 - i * 0.04})`, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', animation: `ringPulse ${2 + i * 0.5}s ${i * 0.3}s ease-in-out infinite` }} />
          ))}
          {/* Spinning ring */}
          <div style={{ position: 'absolute', width: 96, height: 96, borderRadius: '50%', border: '1px solid transparent', borderTopColor: 'rgba(0,212,255,0.8)', borderRightColor: 'rgba(0,255,136,0.4)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', animation: 'spin 3s linear infinite' }} />
          {/* Core */}
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,212,255,0.2) 0%,rgba(0,212,255,0.05) 60%,transparent 100%)', border: '1px solid rgba(0,212,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: speaking ? 'glow 0.8s ease-in-out infinite' : 'glow 2.5s ease-in-out infinite', fontSize: 28 }}>
            ⬡
          </div>
        </div>

        {/* Name */}
        <div style={{ fontFamily: 'Orbitron, Arial', fontSize: 'clamp(28px,5vw,48px)', fontWeight: 900, letterSpacing: 3, lineHeight: 1.15, marginBottom: 14 }}>
          WELCOME,<br />
          <span style={{ color: '#00d4ff', textShadow: '0 0 30px rgba(0,212,255,0.6)' }}>{name.toUpperCase()}</span>
        </div>

        <div style={{ width: 80, height: 2, background: 'linear-gradient(90deg,transparent,#00d4ff,transparent)', margin: '0 auto 24px' }} />

        <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, maxWidth: 520, margin: '0 auto 40px', fontFamily: 'Share Tech Mono' }}>
          {phase === 'intro' && 'Initialising your welcome sequence…'}
          {phase === 'speaking' && 'Playing your personalised welcome message…'}
          {phase === 'done' && 'Your access is confirmed. The mission awaits.'}
        </div>

        {/* Voice bars */}
        {speaking && (
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 4, height: 40, marginBottom: 32 }}>
            {Array.from({ length: 18 }, (_, i) => (
              <div key={i} style={{ width: 4, borderRadius: 2, background: 'linear-gradient(to top,#00ff88,#00d4ff)', height: `${Math.random() * 100}%`, animation: `speakPulse ${0.3 + Math.random() * 0.5}s ${Math.random() * 0.3}s ease-in-out infinite` }} />
            ))}
          </div>
        )}

        {/* Status text */}
        <div style={{ fontSize: 11, color: speaking ? '#00ff88' : phase === 'done' ? '#00d4ff' : 'rgba(255,255,255,0.3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 36 }}>
          {speaking ? '● TRANSMITTING WELCOME' : phase === 'done' ? '✓ WELCOME COMPLETE' : '○ STANDBY'}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {voiceSupported && (
            <button
              onClick={speaking ? stop : playWelcome}
              style={{ padding: '12px 24px', background: speaking ? 'rgba(255,68,68,0.1)' : 'rgba(0,212,255,0.1)', border: `1px solid ${speaking ? 'rgba(255,68,68,0.5)' : 'rgba(0,212,255,0.5)'}`, borderRadius: 6, color: speaking ? '#ff7777' : '#00d4ff', fontFamily: 'Share Tech Mono', fontSize: 12, letterSpacing: 2, cursor: 'pointer', textTransform: 'uppercase' }}
            >
              {speaking ? '⏹ STOP' : '🔊 REPLAY WELCOME'}
            </button>
          )}
          <button
            onClick={() => navigate('/app')}
            style={{ padding: '12px 32px', background: 'linear-gradient(135deg,rgba(0,85,204,0.8),rgba(0,212,255,0.8))', border: '1px solid rgba(0,170,255,0.5)', borderRadius: 6, color: '#fff', fontFamily: 'Share Tech Mono', fontSize: 12, letterSpacing: 2, cursor: 'pointer', textTransform: 'uppercase', fontWeight: 700, boxShadow: '0 0 24px rgba(0,170,255,0.3)' }}
          >
            ENTER THE PLATFORM →
          </button>
        </div>

        {/* Platform URL hint */}
        <div style={{ marginTop: 40, fontSize: 9, color: 'rgba(255,255,255,0.15)', fontFamily: 'Share Tech Mono', letterSpacing: 2 }}>
          NEURALOPS COE · WISSEN TECHNOLOGY · BENGALURU · INTERNAL CONFIDENTIAL
        </div>
      </div>
    </div>
  );
}
